/**
 * Deterministic mock data for the lobby's tabbed Rewards panel.
 *
 * Three series feed three tabs:
 *   • Rewards   — `V2GridRewardEvent[]`   (yield distributions over time)
 *   • Listings  — `SeatData[]`            (current seat self-assessed prices)
 *   • Sales     — `V2GridSeatEvent[]`     (historical buys/buyouts/yield)
 *
 * Same shapes used by the per-grid drawer on `main`, so the lobby and the
 * drawer share the chart / table components verbatim. Phase 2 will swap
 * these generators for live SDK queries without touching `RewardsPanel`.
 */
import type {
  V2GridRewardEvent,
  V2GridSeatEvent,
  V2GridSeatEventType,
} from '@/lib/types/v2';
import type { SeatData } from '@/components/grid';

/** FNV-1a — same hash used by other lobby mock generators for consistency. */
function seed(input: string, salt = 0): number {
  let h = 2166136261 ^ salt;
  for (let i = 0; i < input.length; i++) {
    h = Math.imul(h ^ input.charCodeAt(i), 16777619);
  }
  return h >>> 0;
}

function rng(state: number): () => number {
  let s = state || 1;
  return () => {
    s = Math.imul(s ^ (s >>> 15), 0xa5d3c2f1);
    s = Math.imul(s ^ (s >>> 13), 0xc1c5e3a9);
    return ((s ^ (s >>> 16)) >>> 0) / 0xffffffff;
  };
}

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

/** 30-day window ending now. */
const WINDOW_SECONDS = 30 * 86_400;

const NAME_BANK = [
  'kingmaker', 'legend.eth', 'recruiter', 'oracle.eth', 'pot.king',
  'megadroid', 'soakmode', 'fortuna', 'bigbang', 'whale.dao',
  'beacon.eth', 'silver.spoon', 'mintking', 'astro.farmer', 'plot.king',
];

function pseudoAddress(rand: () => number): `0x${string}` {
  let hex = '';
  for (let i = 0; i < 40; i++) {
    hex += Math.floor(rand() * 16).toString(16);
  }
  return `0x${hex}` as `0x${string}`;
}

function pickName(rand: () => number): string {
  return NAME_BANK[Math.floor(rand() * NAME_BANK.length)];
}

// ─── Rewards ────────────────────────────────────────────────────────────────

/**
 * `amount` is wei-scaled (×1e18) the way `RewardsTab` expects it. Mock
 * boards distribute fee payouts of roughly 0.001–0.5 yield-token units.
 */
export function mockRewardEvents(boardId: string, gridId: string): V2GridRewardEvent[] {
  const rand = rng(seed(boardId, 0xa1));
  const now = Math.floor(Date.now() / 1000);
  const count = 24 + Math.floor(rand() * 14); // 24–37 events
  const events: V2GridRewardEvent[] = [];

  for (let i = 0; i < count; i++) {
    // Cluster timestamps with a slight bias toward the recent half.
    const skew = Math.pow(rand(), 1.4);
    const timestamp = now - Math.floor(skew * WINDOW_SECONDS);
    // 0.001 – 0.5 yield-token units. Logarithmic so most events are small.
    const magnitude = Math.pow(10, -3 + rand() * 2.7);
    const amountWei = BigInt(Math.max(1, Math.floor(magnitude * 1e18)));

    events.push({
      id: `${gridId}-r-${i}`,
      gridId,
      assetHash: null,
      adapterType: 'flaunch',
      payee: ZERO_ADDRESS,
      token: ZERO_ADDRESS,
      amount: amountWei.toString(),
      amountUsd: null,
      timestamp,
      blockNumber: 0,
      txHash: `0x${'0'.repeat(64)}`,
    });
  }

  return events.sort((a, b) => a.timestamp - b.timestamp);
}

// ─── Sales ──────────────────────────────────────────────────────────────────

const SALE_EVENT_TYPES: V2GridSeatEventType[] = [
  'BOUGHT_VACANT',
  'BOUGHT_VACANT',
  'BOUGHT_VACANT',
  'BOUGHT_OUT',
  'YIELD_CLAIMED',
];

/** Prices are 6-decimal USDC bigints — `formatUSD` reads them natively. */
export function mockSaleEvents(boardId: string, gridId: string): V2GridSeatEvent[] {
  const rand = rng(seed(boardId, 0xb2));
  const now = Math.floor(Date.now() / 1000);
  const count = 18 + Math.floor(rand() * 14); // 18–31 events
  const events: V2GridSeatEvent[] = [];

  for (let i = 0; i < count; i++) {
    const skew = Math.pow(rand(), 1.2);
    const timestamp = now - Math.floor(skew * WINDOW_SECONDS);
    const eventType = SALE_EVENT_TYPES[Math.floor(rand() * SALE_EVENT_TYPES.length)];
    const seatIdx = Math.floor(rand() * 100);
    // 5 USDC – 5,000 USDC.
    const usd = 5 + Math.floor(rand() * 4_995);
    const priceMicro = BigInt(usd * 1_000_000).toString();
    const user = pseudoAddress(rand);
    const displayName = pickName(rand);

    events.push({
      id: `${gridId}-s-${i}`,
      seatId: `${gridId}-${seatIdx}`,
      gridId,
      eventType,
      user,
      timestamp,
      blockNumber: 0,
      txHash: `0x${'0'.repeat(64)}`,
      price: eventType === 'YIELD_CLAIMED' ? undefined : priceMicro,
      amount: eventType === 'YIELD_CLAIMED' ? priceMicro : undefined,
      userIdentity: { displayName },
    });
  }

  return events.sort((a, b) => b.timestamp - a.timestamp);
}

// ─── Listings (live seat snapshot) ──────────────────────────────────────────

export function mockSeats(boardId: string, taken: number): SeatData[] {
  const rand = rng(seed(boardId, 0xc3));
  const now = Math.floor(Date.now() / 1000);
  const seats: SeatData[] = [];
  // Cap to one full grid (100 seats) so the mock matches V2_CONSTANTS.
  const occupied = Math.min(100, Math.max(0, taken));

  for (let i = 0; i < occupied; i++) {
    // Self-assessed price: 5 USDC – 5,000 USDC, log-distributed so most
    // listings cluster near the floor. Stored as 6-decimal bigint.
    const usd = 5 + Math.floor(Math.pow(rand(), 1.6) * 4_995);
    const selfAssessed = BigInt(usd * 1_000_000);
    const heldDays = Math.floor(rand() * 60);

    seats.push({
      seatIndex: i,
      holder: pseudoAddress(rand),
      holderName: pickName(rand),
      selfAssessedPrice: selfAssessed,
      balance: selfAssessed / 4n,
      runwaySeconds: 86_400 * (3 + Math.floor(rand() * 21)),
      lastUpdateTime: now - Math.floor(rand() * WINDOW_SECONDS),
      claimedAt: now - heldDays * 86_400,
      lastPriceChangeTime: now - Math.floor(rand() * WINDOW_SECONDS / 2),
      createdAt: now - heldDays * 86_400,
      isForfeited: false,
    });
  }

  return seats;
}
